import { apiClient } from "@/lib/apiClient";
import { MessageRole } from "@/types/chat";

/** AG-UI ToolCall shape — mirrors @ag-ui/core ToolCall exactly. */
export interface ChatHistoryToolCall {
  id: string;
  type: "function";
  function: {
    name: string;
    arguments: string;
  };
}

/** One stored message of the user's active conversation (BFF ChatMessageResDTO). */
export interface ChatHistoryMessage {
  pid: string;
  /** Widened to include "tool" rows emitted by the agent accumulator. */
  role: MessageRole | "tool";
  content: string;
  createdAt: string;
  /** Present on assistant rows that invoked one or more tools. */
  toolCalls?: ChatHistoryToolCall[];
  /** Present on role:"tool" rows — references the assistant toolCall that produced this result. */
  toolCallId?: string;
}

export interface ChatHistoryPage {
  /** Oldest-first (chronological) slice for the requested page. */
  messages: ChatHistoryMessage[];
  /** Total messages in the conversation (for has-more / infinite scroll). */
  total: number;
}

interface ChatMessagesResponse {
  data: ChatHistoryMessage[];
  total: number;
}

/**
 * Fetch one page of the current user's active conversation, returned
 * oldest-first (chronological) for display. `page=1` is the most recent
 * `limit` messages; higher pages are progressively older.
 *
 * The BFF (`GET /api/chat/messages`, AuthGuard cookie) returns newest-first
 * (createdAt DESC) and lazily creates an empty conversation if none exists, so
 * this is safe to call before the user has ever chatted (returns []).
 */
export async function getChatHistory(page = 1, limit = 50): Promise<ChatHistoryPage> {
  const res = await apiClient.get<ChatMessagesResponse>("/api/chat/messages", {
    params: { page: String(page), limit: String(limit) },
  });
  return { messages: [...res.data].reverse(), total: res.total };
}

/**
 * Archive the user's active conversation (server keeps the messages but stops
 * loading them). The next message starts a fresh conversation — used by the
 * "New chat" button.
 */
export async function setConversationInactive(): Promise<void> {
  await apiClient.post<void>("/api/chat/conversations/inactive");
}
